/**
 * RevenuePage — the "Revenue" surface: merges Performance + Listing Intelligence
 * into one decision surface (design handoff "Stayframe - Revenue").
 *
 * Real data via usePerformanceData + useListingIntelligenceData: narrative, KPI
 * strip with sparklines (from trend[]), revenue trend area chart with real
 * period presets, channel mix, AI insight rows, listing-health composite +
 * dimensions + fixes.
 *
 * Honesty per the reconciliation:
 *  - The pricing-decision card (Current→Suggested + Apply to Airbnb) is NET-NEW
 *    (no pricing-suggestion data, no price write-back) → omitted, not faked.
 *  - Listing fixes route to the full Listing studio (fix generator) rather than
 *    a fake "Applied to Airbnb" write-back.
 *  - Forecast, pacing, seasonality, KPI drilldowns, multi-unit switcher, photo
 *    analysis and plan gates are preserved via the "Full analytics" and
 *    "Listing studio" toggles that embed the existing pages.
 */

import { useEffect, useMemo, useState } from "react"
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts"
import { Sparkles, TrendingUp, TrendingDown, ArrowRight, RefreshCw, ArrowLeft, Wand2, LineChart, Check, Plus } from "lucide-react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { cn } from "@/lib/utils"
import { apiClient, type PerformanceKpi } from "@/lib/api"
import { useTasks } from "@/pages/tasks/useTasks"
import { usePerformanceData } from "@/pages/performance/usePerformanceData"
import { useListingIntelligenceData } from "@/pages/listings/useListingIntelligenceData"
import { PerformancePage } from "@/pages/performance/PerformancePage"
import { ListingIntelligencePage } from "@/pages/listings/ListingIntelligencePage"

const eyebrow = "text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground"
const brandEyebrow = "inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-brand-strong"

const PERIOD_TABS = [
  { label: "Next 12mo", preset: "next12" as const },
  { label: "This year", preset: "this_year" as const },
  { label: "This month", preset: "this_month" as const },
]

const CHANNEL_COLOR: Record<string, string> = {
  airbnb: "var(--sf-airbnb)",
  "booking.com": "var(--sf-booking)",
  booking: "var(--sf-booking)",
  direct: "var(--sf-brand)",
}

const INSIGHT_TONE = {
  opportunity: { label: "Opportunity", bg: "bg-brand-soft", text: "text-brand-strong" },
  watch: { label: "Watch", bg: "bg-amber-soft", text: "text-amber-strong" },
  info: { label: "Insight", bg: "bg-sky-soft", text: "text-sky" },
}

const SCORE_ACCENT = (label: string): string => {
  const l = label.toLowerCase()
  if (l.includes("excellent") || l.includes("good")) return "bg-brand"
  if (l.includes("needs")) return "bg-amber"
  if (l.includes("poor") || l.includes("critical")) return "bg-rose"
  return "bg-line-2"
}
const SCORE_TEXT = (label: string): string => {
  const l = label.toLowerCase()
  if (l.includes("excellent") || l.includes("good")) return "text-brand-strong"
  if (l.includes("needs")) return "text-amber-strong"
  if (l.includes("poor") || l.includes("critical")) return "text-rose-strong"
  return "text-muted-foreground"
}
const ISSUE_TONE: Record<string, { dot: string; text: string; bg: string; label: string }> = {
  critical: { dot: "bg-rose", text: "text-rose-strong", bg: "bg-rose-soft", label: "Critical" },
  warning: { dot: "bg-amber", text: "text-amber-strong", bg: "bg-amber-soft", label: "Warning" },
  suggestion: { dot: "bg-brand", text: "text-brand-strong", bg: "bg-brand-soft", label: "Suggestion" },
}

function pctFmt(v: number | null | undefined): string | null {
  if (v == null) return null
  const pct = Math.abs(v) <= 1 ? v * 100 : v
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`
}

function Sparkline({ series }: { series: number[] }) {
  const clean = series.filter((n) => Number.isFinite(n))
  if (clean.length < 2) return null
  const w = 100, h = 32, p = 4
  const min = Math.min(...clean), max = Math.max(...clean), span = max - min || 1
  const step = (w - p * 2) / (clean.length - 1)
  const points = clean.map((v, i) => `${(p + i * step).toFixed(1)},${(h - p - ((v - min) / span) * (h - p * 2)).toFixed(1)}`).join(" ")
  return (
    <svg width="100" height="32" viewBox="0 0 100 32" fill="none" className="flex-none">
      <polyline points={points} fill="none" stroke="var(--sf-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

interface RevenueGap {
  start: string
  end: string
  nights: number
  open_ended: boolean
}

export function RevenuePage() {
  const { propertyId, property } = usePropertyContext()
  const perf = usePerformanceData()
  const listing = useListingIntelligenceData(propertyId)
  const { tasks, addTask } = useTasks()
  const [mode, setMode] = useState<"summary" | "analytics" | "listing">("summary")

  // Real gap detection (net-30-days unbooked stretches) + achieved-ADR context.
  const [gaps, setGaps] = useState<RevenueGap[]>([])
  const [adrRecent, setAdrRecent] = useState<number | null>(null)
  useEffect(() => {
    if (!propertyId) return
    let cancelled = false
    apiClient.getRevenueGaps(propertyId).then((res) => {
      if (cancelled || !res.ok) return
      setGaps(Array.isArray(res.gaps) ? res.gaps : [])
      setAdrRecent(res.adr_recent ?? null)
    }).catch(() => {})
    return () => {
      cancelled = true
    }
  }, [propertyId])

  const fmtCur = (kpi: PerformanceKpi | undefined) =>
    kpi?.value == null ? "—" : perf.fmtCurrency.format(kpi.value)
  const fmtPctVal = (kpi: PerformanceKpi | undefined) => {
    // worker.js sends occupancy as an already-scaled 0–100 percentage
    if (!kpi || kpi.value == null) return "—"
    return `${Math.round(kpi.value)}%`
  }

  // Forward-looking windows have no meaningful vs-previous delta on point KPIs
  // (the old Performance page used pacing instead) — suppress rather than mislead.
  const isForward = perf.period === "next12"

  const kpis = useMemo(() => {
    const k = perf.kpis
    const t = perf.trend || []
    if (!k) return []
    const d = (kpi: PerformanceKpi | undefined) => (isForward ? null : kpi?.delta)
    return [
      { label: "Revenue", value: fmtCur(k.revenue), delta: d(k.revenue), series: t.map((p) => p.revenue), note: isForward ? "booked, next 12 months" : "vs previous period" },
      { label: "Occupancy", value: fmtPctVal(k.occupancy), delta: d(k.occupancy), series: t.map((p) => p.occupancy), note: "nights booked" },
      { label: "ADR", value: fmtCur(k.adr), delta: d(k.adr), series: t.map((p) => p.adr), note: "average nightly rate" },
      { label: "RevPAR", value: fmtCur(k.revpar), delta: d(k.revpar), series: t.map((p) => p.revpar), note: "per available night" },
      { label: "Bookings", value: k.bookings?.value == null ? "—" : String(Math.round(k.bookings.value)), delta: d(k.bookings), series: t.map((p) => p.bookings), note: "confirmed bookings" },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perf.kpis, perf.trend, perf.currency, isForward])

  const insightRows = useMemo(() => {
    const recs = perf.insightResult?.recommendations || []
    if (recs.length > 0) {
      return recs.slice(0, 3).map((r) => ({
        tone: r.priority === "act_now" ? ("opportunity" as const) : ("watch" as const),
        text: r.action,
      }))
    }
    const wmn = perf.insightResult?.human_summary?.what_matters_now || []
    return wmn.slice(0, 3).map((text) => ({ tone: "info" as const, text }))
  }, [perf.insightResult])

  const narrative = useMemo(() => {
    const wmn = perf.insightResult?.human_summary?.what_matters_now?.[0]
    if (wmn) return wmn
    const pace = pctFmt(perf.pacing?.delta_pct)
    if (pace) return `Revenue is pacing ${pace} versus the same period last year.`
    const top = perf.channels?.[0]
    if (top) return `${top.name} currently drives ${Math.round(top.pct)}% of booked revenue.`
    return "Commercial signals sharpen as more bookings and pacing data come in."
  }, [perf.insightResult, perf.pacing, perf.channels])

  // Embedded full tools (preserve forecast/pacing/drilldowns + fix studio/multi-unit/gate)
  if (mode !== "summary") {
    const Embedded = mode === "analytics" ? PerformancePage : ListingIntelligencePage
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 pt-6 md:px-7">
        <button
          type="button"
          onClick={() => setMode("summary")}
          className="mb-2 inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-card px-3 py-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
          Back to Revenue
        </button>
        <Embedded />
      </div>
    )
  }

  const revenueKpi = perf.kpis?.revenue
  const gradeColor = listing.grade === "A" || listing.grade === "B" ? "text-brand-strong" : listing.grade === "C" ? "text-amber-strong" : "text-rose-strong"

  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-6 md:px-7">
      {/* NARRATIVE */}
      <section className="mb-[18px] rounded-card border border-border bg-card px-6 py-5 shadow-sm">
        <div className={brandEyebrow}>
          <Sparkles className="h-3 w-3 fill-current" strokeWidth={0} />
          The narrative
        </div>
        <p className="mt-2.5 max-w-[74ch] text-[17px] leading-[1.5] text-foreground">{narrative}</p>
      </section>

      {/* KPI STRIP */}
      {kpis.length > 0 ? (
        <section className="mb-[18px] grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-5">
          {kpis.map((k) => (
            <article key={k.label} className="rounded-card border border-border bg-card px-4 pb-3 pt-4 shadow-sm">
              <div className="flex items-start justify-between">
                <span className="text-[12px] font-semibold text-muted-foreground">{k.label}</span>
                {k.delta != null && (
                  <span className={cn("inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold", k.delta >= 0 ? "bg-brand-soft text-brand-strong" : "bg-rose-soft text-rose-strong")}>
                    {k.delta >= 0 ? <TrendingUp className="h-2.5 w-2.5" strokeWidth={2.4} /> : <TrendingDown className="h-2.5 w-2.5" strokeWidth={2.4} />}
                    {k.delta >= 0 ? "+" : ""}{k.delta.toFixed(1)}%
                  </span>
                )}
              </div>
              <div className="mt-2.5 flex items-end justify-between gap-2">
                <strong className="font-display text-[26px] font-bold tracking-[-0.02em] text-foreground">{k.value}</strong>
                <Sparkline series={k.series} />
              </div>
              <p className="mt-2 text-[11.5px] font-medium text-muted-foreground">{k.note}</p>
            </article>
          ))}
        </section>
      ) : perf.loading ? (
        <section className="mb-[18px] grid grid-cols-2 gap-3.5 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-[120px] animate-pulse rounded-card bg-line-soft" />)}
        </section>
      ) : null}

      {/* REVENUE TREND */}
      <section className="mb-[18px] rounded-card border border-border bg-card px-[22px] py-5 shadow-sm">
        <div className="mb-[18px] flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className={cn(eyebrow, "mb-1")}>Revenue trend</div>
            <div className="flex items-baseline gap-2.5">
              <strong className="font-display text-[28px] font-bold tracking-[-0.02em] text-foreground">{fmtCur(revenueKpi)}</strong>
              {!isForward && revenueKpi?.delta != null && (
                <span className={cn("inline-flex items-center gap-0.5 text-[13px] font-semibold", revenueKpi.delta >= 0 ? "text-brand-strong" : "text-rose-strong")}>
                  {revenueKpi.delta >= 0 ? <TrendingUp className="h-3 w-3" strokeWidth={2.4} /> : <TrendingDown className="h-3 w-3" strokeWidth={2.4} />}
                  {revenueKpi.delta >= 0 ? "+" : ""}{revenueKpi.delta.toFixed(1)}% vs last year
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 rounded-[10px] bg-line-soft p-[3px]">
              {PERIOD_TABS.map((t) => (
                <button
                  key={t.preset}
                  type="button"
                  onClick={() => perf.setPeriod(t.preset)}
                  className={cn("rounded-[8px] px-3 py-1.5 text-[12px] font-semibold transition-all", perf.period === t.preset ? "bg-foreground text-white" : "text-ink-2 hover:text-foreground")}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setMode("analytics")}
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
            >
              <LineChart className="h-3.5 w-3.5" strokeWidth={1.9} />
              Full analytics
            </button>
          </div>
        </div>
        {perf.trend && perf.trend.length > 1 ? (
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={perf.trend} margin={{ top: 6, right: 6, left: -6, bottom: 0 }}>
                <defs>
                  <linearGradient id="revfill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--sf-brand)" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="var(--sf-brand)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="var(--sf-line)" />
                <XAxis dataKey="period" tickLine={false} axisLine={false} tick={{ fontFamily: "var(--sf-font-mono)", fontSize: 10, fill: "var(--sf-muted)" }} interval="preserveStartEnd" />
                <YAxis tickLine={false} axisLine={false} width={46} tick={{ fontFamily: "var(--sf-font-mono)", fontSize: 10, fill: "var(--sf-muted)" }} tickFormatter={(v: number) => (v === 0 ? "€0" : `€${Math.round(v / 1000)}k`)} />
                <Area type="monotone" dataKey="revenue" stroke="var(--sf-brand)" strokeWidth={2.5} fill="url(#revfill)" dot={false} activeDot={{ r: 4.5, fill: "var(--sf-brand)", stroke: "#fff", strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex h-[200px] items-center justify-center text-[13px] text-muted-foreground">
            {perf.loading ? "Loading trend…" : "No revenue trend for this period yet."}
          </div>
        )}
      </section>

      {/* CHANNEL MIX + AI INSIGHTS */}
      <section className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1fr_1.3fr]">
        <div className="rounded-card border border-border bg-card p-5 shadow-sm">
          <div className={cn(eyebrow, "mb-1")}>Channel mix</div>
          <h3 className="mb-[18px] font-display text-[17px] font-bold tracking-[-0.02em] text-foreground">Where bookings come from</h3>
          {perf.channels && perf.channels.length > 0 ? (
            <div className="flex flex-col gap-4">
              {perf.channels.map((c) => {
                const color = CHANNEL_COLOR[c.name.toLowerCase()] || "var(--sf-muted)"
                return (
                  <div key={c.name}>
                    <div className="mb-[7px] flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-foreground">
                        <span className="h-[9px] w-[9px] rounded-[3px]" style={{ background: color }} />
                        {c.name}
                      </span>
                      <span className="text-[12.5px] font-semibold text-ink-2">{perf.fmtCurrency.format(c.revenue)}</span>
                    </div>
                    <div className="h-[9px] overflow-hidden rounded-full bg-line-soft">
                      <div className="h-full rounded-full" style={{ width: `${Math.round(c.pct)}%`, background: color }} />
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 text-[11px] font-medium text-muted-foreground">
                      <span>{Math.round(c.pct)}% of revenue</span>
                      {c.adr > 0 && <span>ADR {perf.fmtCurrency.format(c.adr)}</span>}
                      {c.cancellation_rate != null && c.cancellation_rate > 0 && (
                        <span className={cn(c.cancellation_rate >= 15 && "text-amber-strong")}>
                          {Math.round(c.cancellation_rate)}% cancel
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
              {perf.channels[0] && perf.channels[0].pct >= 65 && (
                <div className="flex items-start gap-2 rounded-[11px] bg-amber-soft px-3 py-2.5 text-[12px] leading-[1.45] text-ink-2">
                  <span className="mt-[5px] h-1.5 w-1.5 flex-none rounded-full bg-amber" />
                  <span>
                    <strong className="font-semibold text-foreground">{perf.channels[0].name}</strong> drives{" "}
                    {Math.round(perf.channels[0].pct)}% of revenue — concentration risk if that channel wobbles. Growing
                    direct bookings reduces both risk and fees.
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="py-6 text-center text-[12.5px] text-muted-foreground">Channel mix appears once bookings are synced.</p>
          )}
        </div>
        <div className="rounded-card border border-border bg-card p-5 shadow-sm">
          <div className={cn(brandEyebrow, "mb-1")}>
            <Sparkles className="h-3 w-3 fill-current" strokeWidth={0} />
            AI insights
          </div>
          <h3 className="mb-4 font-display text-[17px] font-bold tracking-[-0.02em] text-foreground">What to do about it</h3>
          {insightRows.length > 0 ? (
            <div className="flex flex-col gap-[11px]">
              {insightRows.map((row, i) => {
                const tone = INSIGHT_TONE[row.tone]
                return (
                  <div key={i} className={cn("flex gap-3 rounded-[13px] p-[13px_14px]", tone.bg)}>
                    <span className={cn("min-w-[74px] flex-none pt-px text-[10.5px] font-semibold uppercase tracking-[0.04em]", tone.text)}>{tone.label}</span>
                    <p className="text-[12.5px] leading-[1.5] text-foreground">{row.text}</p>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="py-6 text-center text-[12.5px] text-muted-foreground">{perf.insightsLoading ? "Generating insights…" : "AI insights appear once there's enough booking history."}</p>
          )}
        </div>
      </section>

      {/* PRICING DECISION — real gap detection + tracked follow-up.
          (Rate suggestions + channel write-back remain deferred: no rate engine / channel API yet.) */}
      {gaps.length > 0 && (() => {
        const gap = gaps[0]
        const fmtDay = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })
        const taskId = `gap-${propertyId}-${gap.start}`
        const created = tasks.some((t) => t.id === taskId)
        return (
          <section className="mb-[18px] rounded-card border border-amber px-[22px] py-5 shadow-sm" style={{ background: "linear-gradient(180deg,var(--sf-amber-soft),var(--sf-card) 60%)" }}>
            <div className="flex flex-wrap items-center gap-[18px]">
              <div className="min-w-[260px] flex-1">
                <div className="mb-1.5 inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-amber-strong">
                  <span className="h-[7px] w-[7px] rounded-full bg-amber" />
                  Pricing decision
                </div>
                <h3 className="font-display text-[17px] font-bold tracking-[-0.01em] text-foreground">
                  Open gap · {fmtDay(gap.start)} – {fmtDay(gap.end)}
                </h3>
                <p className="mt-1.5 max-w-[60ch] text-[13px] leading-[1.5] text-ink-2">
                  {gap.nights} night{gap.nights === 1 ? "" : "s"} unbooked in the next 30 days
                  {adrRecent != null && (
                    <> — your achieved rate over the last 90 days is <strong className="font-semibold text-foreground" style={{ fontWeight: 650 }}>{perf.fmtCurrency.format(adrRecent)}/night</strong> (~{perf.fmtCurrency.format(adrRecent * gap.nights)} at stake)</>
                  )}
                  . Review the rate on your channel and track it here until it's handled.
                </p>
              </div>
              <button
                type="button"
                disabled={created}
                onClick={() =>
                  addTask({
                    id: taskId,
                    propertyId,
                    propName: property?.name || "Property",
                    source: "Pricing",
                    srcKey: "pricing",
                    urgent: gap.nights >= 4,
                    title: `Re-price open gap · ${fmtDay(gap.start)} – ${fmtDay(gap.end)}`,
                    detail: `${gap.nights} unbooked night${gap.nights === 1 ? "" : "s"}${adrRecent != null ? ` · ~${perf.fmtCurrency.format(adrRecent * gap.nights)} at your achieved rate` : ""}.`,
                    cta: "Open Revenue",
                    ctaSurface: "performance",
                    status: "todo",
                  })
                }
                className={cn(
                  "inline-flex flex-none items-center gap-1.5 rounded-[11px] px-[17px] py-[11px] text-[13px] font-semibold transition-opacity",
                  created ? "bg-brand text-white" : "bg-forest text-white hover:opacity-90",
                )}
              >
                {created ? <Check className="h-[15px] w-[15px]" strokeWidth={2.4} /> : <Plus className="h-[15px] w-[15px]" strokeWidth={2.2} />}
                {created ? "Tracked in Tasks" : "Track as task"}
              </button>
            </div>
          </section>
        )
      })()}

      {/* LISTING HEALTH */}
      <section className="mb-[18px] rounded-card border border-border bg-card p-[22px] shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className={cn(brandEyebrow, "mb-1.5")}>
              <Sparkles className="h-3 w-3 fill-current" strokeWidth={0} />
              Listing health
            </div>
            {listing.hasAudit ? (
              <div className="flex items-baseline gap-2.5">
                <strong className="font-display text-[30px] font-bold tracking-[-0.02em] text-foreground">{listing.compositeScore}</strong>
                <span className={cn("font-display text-[18px] font-bold", gradeColor)}>{listing.grade}</span>
                {listing.scoreDelta != null && listing.scoreDelta !== 0 && (
                  <span className={cn("inline-flex items-center gap-0.5 text-[12.5px] font-semibold", listing.scoreDelta >= 0 ? "text-brand-strong" : "text-rose-strong")}>
                    {listing.scoreDelta >= 0 ? <TrendingUp className="h-3 w-3" strokeWidth={2.4} /> : <TrendingDown className="h-3 w-3" strokeWidth={2.4} />}
                    {listing.scoreDelta >= 0 ? "+" : ""}{listing.scoreDelta} this week
                  </span>
                )}
              </div>
            ) : (
              <p className="mt-1 text-[13px] text-muted-foreground">{listing.loading ? "Loading listing audit…" : "Run an audit to score your listing across content, amenities, reviews, seasonality and photos."}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {listing.hasAudit && (
              <button
                type="button"
                onClick={listing.runAudit}
                disabled={listing.isAuditing || listing.auditCooldown}
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-border bg-card px-3.5 py-2 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-line-soft disabled:opacity-50"
              >
                <RefreshCw className={cn("h-3.5 w-3.5", listing.isAuditing && "animate-spin")} strokeWidth={2} />
                {listing.isAuditing ? "Auditing…" : "Re-audit"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setMode("listing")}
              className="inline-flex items-center gap-1.5 rounded-[10px] bg-forest px-3.5 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
            >
              Listing studio
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          </div>
        </div>
        {listing.hasAudit && (
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
            {listing.dimensions.map((d) => (
              <div key={d.key} className="relative overflow-hidden rounded-[13px] border border-border p-[14px_13px]">
                <div className={cn("absolute inset-y-0 left-0 w-[3px]", SCORE_ACCENT(d.scoreLabel))} />
                <div className="text-[11px] font-semibold leading-[1.25] text-ink-2">{d.label}</div>
                <div className="mt-1.5 font-display text-[22px] font-bold text-foreground">{d.score > 0 ? d.score : "—"}</div>
                <div className={cn("mt-0.5 text-[10.5px] font-semibold", SCORE_TEXT(d.scoreLabel))}>{d.scoreLabel}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* FIXES */}
      {listing.hasAudit && listing.issues.length > 0 && (
        <section className="rounded-card border border-border bg-card p-[22px] shadow-sm">
          <h3 className="mb-1 font-display text-[18px] font-bold tracking-[-0.02em] text-foreground">Fixes that push you toward an A</h3>
          <p className="mb-4 text-[13px] text-muted-foreground">Generated from your reviews, rules and season profile — open the studio to generate and apply copy.</p>
          <div className="flex flex-col gap-[11px]">
            {listing.issues.slice(0, 5).map((f) => {
              const tone = ISSUE_TONE[f.severity] || ISSUE_TONE.suggestion
              return (
                <article key={f.id} className="flex items-start gap-3.5 rounded-2xl border border-border p-4">
                  <span className={cn("mt-[3px] h-[9px] w-[9px] flex-none rounded-full", tone.dot)} />
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2.5">
                      <span className="text-[14px] font-semibold text-foreground" style={{ fontWeight: 650 }}>{f.title}</span>
                      <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em]", tone.bg, tone.text)}>{tone.label}</span>
                      {f.category && <span className="text-[11.5px] font-medium text-muted-foreground">{f.category}</span>}
                    </div>
                    <p className="text-[12.5px] leading-[1.5] text-ink-2">{f.description}</p>
                  </div>
                  {f.fixable && (
                    <button
                      type="button"
                      onClick={() => setMode("listing")}
                      className="inline-flex h-fit flex-none items-center gap-1.5 whitespace-nowrap rounded-[9px] border border-brand bg-brand px-3 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
                    >
                      <Wand2 className="h-3.5 w-3.5" strokeWidth={2} />
                      Generate fix
                    </button>
                  )}
                </article>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}
